/**
 * FamilyContextCollector
 *
 * Client-side helper that gathers family context (members, calendar mappings)
 * for inclusion in API calls (e.g. home summary generation).
 */

import type { LocalFamilyMembersStore } from '@/lib/local/LocalFamilyMembersStore';
import type { LocalCalendarMemberMappingStore } from '@/lib/local/LocalCalendarMemberMappingStore';

export type CollectedFamilyContext = {
  /** Map of family member ID → display name ("You" for the Me member) */
  familyMembers: Record<string, string>;
  /** Map of family member ID → array of discrete notes for AI context */
  familyMemberNotes: Record<string, string[]>;
  /** Map of device calendar ID → array of family member IDs */
  calendarMemberMappings: Record<string, string[]>;
  /** The family member ID that represents "Me" */
  meFamilyMemberId: string;
};

export class FamilyContextCollector {
  /**
   * Collect family context from local stores.
   * Returns null if stores are not available.
   */
  static async collect(
    familyMembersStore: LocalFamilyMembersStore | null,
    calendarMappingStore: LocalCalendarMemberMappingStore | null,
  ): Promise<CollectedFamilyContext | null> {
    if (!familyMembersStore || !calendarMappingStore) return null;

    const [members, mappings] = await Promise.all([
      familyMembersStore.getAll(),
      calendarMappingStore.getAll(),
    ]);

    const meMember = members.find(m => m.isMe);
    if (!meMember) return null;

    const familyMembers: Record<string, string> = {};
    const familyMemberNotes: Record<string, string[]> = {};
    for (const member of members) {
      familyMembers[member.id] = member.isMe ? 'You' : member.name;
      if (member.notes && member.notes.length > 0) {
        familyMemberNotes[member.id] = member.notes;
      }
    }

    const calendarMemberMappings: Record<string, string[]> = {};
    for (const mapping of mappings) {
      calendarMemberMappings[mapping.calendarId] = mapping.memberIds;
    }

    return {
      familyMembers,
      familyMemberNotes,
      calendarMemberMappings,
      meFamilyMemberId: meMember.id,
    };
  }
}
